"use client";

import clsx from "clsx";
import { Monogram } from "@/components/persona/Monogram";
import { BankBadge } from "@/components/banks/BankBadge";
import { TradeDossier } from "@/components/sign/TradeDossier";
import { useTranslations } from "@/lib/compat/i18n";

/**
 * Screen 4.7 — T2 reveal. Al Madar's counterparty profile compounds:
 *   1 → 2 trades · USD 1.9M lifetime · 100% on-time · 50% bank-financed (FAB).
 *   Aurora's profile updates: 25 → 26 trades · USD 15.65M → USD 16.7M lifetime.
 *
 * Living-reputation moment: the second trade visibly compounds. The audience
 * feels the difference — this is what the reputation primitive does.
 */
export type ReputationDelta = {
  before: Record<string, unknown>;
  after: Record<string, unknown>;
};

export function T2Reveal({
  alMadar,
  aurora,
  className,
}: {
  alMadar: ReputationDelta | null;
  aurora: ReputationDelta | null;
  className?: string;
}) {
  const t = useTranslations("t2Scenario");
  return (
    <div className={clsx("flex h-full flex-col gap-4", className)}>
      <div className="glass relative overflow-hidden p-5">
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-state-financed to-transparent" />
        <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{t("revealKicker")}</p>
        <h2 className="mt-1 font-display text-2xl font-semibold text-orbitra-paper">
          {t("revealTitle")}
        </h2>
        <p className="mt-1 text-[12px] text-orbitra-teal-soft">
          {t("revealSummary")}
        </p>
        <div className="mt-3 flex items-center gap-2">
          <BankBadge name="FAB" fundingPct={30} variant="edge" />
          <span className="rounded-full border border-state-financed/40 bg-state-financed/10 px-3 py-1 font-mono text-[10px] uppercase tracking-wider text-state-financed">
            LC-2026-00187 · USD 1,500,000
          </span>
        </div>
      </div>

      <div className="grid flex-1 grid-cols-2 gap-4">
        <ReputationPane
          name={t("revealPaneAlMadarName")}
          monogramName="Al Madar Foods"
          flag="AE"
          headline={t("revealPaneHeadline")}
          before={alMadar?.before ?? { verifiedTrades: 1, lifetimeVolumeUsd: 850_000, selfFinancedPct: 100, bankFinancedPct: 0 }}
          after={alMadar?.after   ?? { verifiedTrades: 2, lifetimeVolumeUsd: 2_350_000, onTimePct: 100, bankFinancedPct: 19, bankPartnersList: ["FAB"] }}
          arc={t("revealPaneArcAlMadar")}
          showBankPartner
        />
        <ReputationPane
          name={t("revealPaneAuroraName")}
          monogramName="Aurora Aves Brasil"
          flag="BR"
          headline={t("revealPaneHeadline")}
          before={aurora?.before ?? { verifiedTrades: 25, lifetimeVolumeUsd: 15_650_000 }}
          after={aurora?.after   ?? { verifiedTrades: 26, lifetimeVolumeUsd: 17_150_000 }}
          arc={t("revealPaneArcAurora")}
        />
      </div>

      <TradeDossier scenarioId="t2-bank-financed" />

      <div className="glass flex items-center gap-4 px-4 py-3 text-[11px]">
        <span className="text-orbitra-paper">
          {t("revealFooterTimeLabel")} <span className="font-mono text-state-traced">{t("revealFooterTimeValue")}</span>
        </span>
        <span className="text-orbitra-muted">·</span>
        <span className="text-orbitra-muted">
          {t("revealFooterPreOrbitraLabel")} <span className="font-mono">{t("revealFooterPreOrbitraValue")}</span>
        </span>
        <span className="text-orbitra-muted">·</span>
        <span className="text-orbitra-muted">
          {t("revealFooterFabIntegration")} <span className="font-mono">{t("revealFooterFabIntegrationValue")}</span>
        </span>
      </div>
    </div>
  );
}

function ReputationPane({
  name,
  monogramName,
  flag,
  headline,
  before,
  after,
  arc,
  showBankPartner,
}: {
  name: string;
  monogramName: string;
  flag: string;
  headline: string;
  before: Record<string, unknown>;
  after: Record<string, unknown>;
  arc: string;
  showBankPartner?: boolean;
}) {
  const t = useTranslations("t2Scenario");
  return (
    <div className="glass flex flex-col p-5">
      <div className="flex items-center gap-3">
        <Monogram name={monogramName} flag={flag} size={40} accent="var(--state-verified)" />
        <div>
          <p className="font-mono text-[9px] uppercase tracking-[0.18em] text-state-financed">{headline}</p>
          <p className="mt-0.5 font-display text-base font-semibold text-orbitra-paper">{name}</p>
        </div>
      </div>

      <div className="mt-4 grid grid-cols-2 gap-3 text-[11px]">
        <Stat label={t("statVerifiedTrades")}    beforeV={fmt(before.verifiedTrades)}       afterV={fmt(after.verifiedTrades)} />
        <Stat label={t("statLifetimeVolume")}    beforeV={fmtUsd(before.lifetimeVolumeUsd)} afterV={fmtUsd(after.lifetimeVolumeUsd)} />
        <Stat label={t("statOnTimeSettlement")}  beforeV={fmtPct(before.onTimePct)}         afterV={fmtPct(after.onTimePct)} />
        <Stat label={t("statBankFinancedShare")} beforeV={fmtPct(before.bankFinancedPct)}   afterV={fmtPct(after.bankFinancedPct)} />
      </div>

      {showBankPartner && (
        <div className="mt-3 flex items-center gap-2 rounded-md border border-state-financed/30 bg-state-financed/8 px-3 py-2 text-[11px] text-state-financed">
          <span className="font-mono text-[10px] uppercase tracking-wider">{t("statBankPartnersOnFile")}</span>
          <BankBadge name="FAB" variant="compact" />
        </div>
      )}

      <div className="mt-auto rounded-md border border-state-financed/30 bg-state-financed/8 p-3 text-[11px] text-state-financed">
        {t("reputationArcPrefix")}<span className="font-mono">{arc}</span>
      </div>
    </div>
  );
}

function Stat({ label, beforeV, afterV }: { label: string; beforeV: string; afterV: string }) {
  const changed = beforeV !== afterV;
  return (
    <div className="rounded-md border border-state-verified/20 bg-orbitra-ink/40 px-3 py-2">
      <p className="text-[9px] uppercase tracking-[0.18em] text-orbitra-muted">{label}</p>
      <div className="mt-1 flex items-baseline gap-2">
        <span className={clsx("font-mono text-[11px]", changed ? "text-orbitra-muted line-through" : "text-orbitra-paper")}>{beforeV}</span>
        {changed && <span className="font-mono text-[10px] text-state-financed">→</span>}
        {changed && <span className="font-display text-base font-semibold text-state-financed">{afterV}</span>}
      </div>
    </div>
  );
}

function fmt(v: unknown): string { return v === undefined || v === null ? "—" : String(v); }
function fmtUsd(v: unknown): string {
  if (v === undefined || v === null) return "—";
  const n = Number(v); if (!Number.isFinite(n)) return String(v);
  if (n >= 1_000_000) return `USD ${(n / 1_000_000).toFixed(2)}M`;
  if (n >= 1_000)     return `USD ${(n / 1_000).toFixed(0)}K`;
  return `USD ${n}`;
}
function fmtPct(v: unknown): string { return v === undefined || v === null ? "—" : `${v}%`; }
